import { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ListRow } from 'basic-components';
import { FatArrowDown, Info, Remove } from 'basic-components/icons';

import { creatorSeriesActions } from 'actions/series';
import { FormulaUtils } from 'utils';

import SymbolColumns from './SymbolColumns';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};

const Row = ({ symbols = EMPTY_OBJ, metadata = EMPTY_OBJ, groupName = undefined, pattern = "true", query = undefined, scrollRef = EMPTY_OBJ, columns = EMPTY_ARRAY, value = undefined, index, swapIsInfoModalOpen, setSelectedSymbolIndex, isPattern = false, source = undefined, formulaExpressions = {} }) => {
  const dispatch = useDispatch(),
        removeItem = useCallback(() => dispatch(creatorSeriesActions.swapSelectedSymbol({ symbols, metadata, groupName, pattern, query, source })), [dispatch, symbols, metadata, groupName, pattern, query, source]),
        columnsOverridden = useMemo(() => FormulaUtils.isRawFormulaOverridden(formulaExpressions.columns), [formulaExpressions.columns]),
        symbolValue = useMemo(() => {
          if(symbols?.Symbol) {
            return symbols.Symbol
          }
          const entries = Object.entries(symbols || {})
          if (entries.length === 0) {
            return ""
          }
          const [firstKey, firstValue] = entries[0]
          return `${firstKey}: ${firstValue}`
        }, [symbols]),
        areColumnCustom = useMemo(() => columns.length > 0, [columns]),
        [areColumnsOpen, setAreColumnsOpen] = useState(false),
        swapAreColumnsOpen = useCallback(() => setAreColumnsOpen(areOpen => !areOpen), []),
        openModal = useCallback(() => {
          setSelectedSymbolIndex(index)
          swapIsInfoModalOpen()
        }, [index, setSelectedSymbolIndex, swapIsInfoModalOpen]),
        metadataValues = useMemo(() => Object.values(metadata), [metadata]),
        metadataValuesLength = useMemo(() => {
          let toReturn = metadataValues?.length
          if(groupName) {
            toReturn += 1
          }
          if(query) {
            toReturn += 1
          }
          return toReturn
        }, [groupName, metadataValues, query]),
        handleOnInfoKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            openModal()
          }
        }, [openModal]),
        handleOnRmoveKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            removeItem()
          }
        }, [removeItem]),
        handleOnColumnsKeyDown = useCallback(e => {
          if(columnsOverridden) {
            return
          }
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapAreColumnsOpen()
          }
        }, [columnsOverridden, swapAreColumnsOpen]),
        patternValue = useMemo(() => metadataValues?.length > 0 ? String(metadataValues[0]) : (groupName || query), [groupName, metadataValues, query]),
        labelValue = useMemo(() => isPattern ? patternValue : symbolValue + " " + groupName, [groupName, isPattern, patternValue, symbolValue])

  return (
    <Fragment>
      <ListRow
          className={classNames(
            "ng-office-app__authed__content__basket__body__data__basket__items__item",
            { "ng-office-app__authed__content__basket__body__data__basket__items__item--with-counter": metadataValuesLength > 1 }
          )}
          offset={8000}
          scrollContRef={scrollRef}
      >
        <div
            aria-label={`Open ${labelValue} info modal`}
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__info"
            onClick={openModal}
            onKeyDown={handleOnInfoKeyDown}
            role="button"
            tabIndex={0}
        >
          <Info
              color="blue-bright"
              height={16}
              width={16}
          />
        </div>
        <If condition={isPattern}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {patternValue}
                </div>
                <When condition={metadataValuesLength > 1}>
                    {() => (
                      <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell ng-office-app__authed__content__basket__body__data__basket__items__item__cell--subcell">
                        {`${metadataValuesLength - 1} more...`}
                      </div>
                    )}
                </When>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Fragment>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {groupName}
                </div>
                <div className="ng-office-app__authed__content__basket__body__data__basket__items__item__cell">
                  {symbolValue}
                </div>
              </Fragment>
            )}
          </Else>
        </If>
        <div
            aria-disabled={columnsOverridden}
            aria-label={`Select columns ${labelValue}`}
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__columns"
            onClick={columnsOverridden ? undefined : swapAreColumnsOpen}
            onKeyDown={handleOnColumnsKeyDown}
            role="button"
            tabIndex={columnsOverridden ? -1 : 0}
        >
          <div 
              className={classNames(
                "ng-office-app__authed__content__basket__body__data__basket__items__item__columns__label",
                { "ng-office-app__authed__content__basket__body__data__basket__items__item__columns__label--is-disabled": columnsOverridden },
              )}
          >
            {columnsOverridden ? FormulaUtils.OVERRIDDEN : areColumnCustom ? "Custom" : "Default"}
          </div>
          <FatArrowDown
              color="black-base"
              height={12}
              width={12}
          />
        </div>
        <div
            aria-label={`Remove ${labelValue}`}
            className="ng-office-app__authed__content__basket__body__data__basket__items__item__action"
            onClick={removeItem}
            onKeyDown={handleOnRmoveKeyDown}
            role="button"
            tabIndex={0}
        >
          <Remove
              color="red-dark"
              height={16}
              width={16}
          />
        </div>
      </ListRow>
      <When condition={areColumnsOpen}>
        {() => (
          <SymbolColumns
              groupName={groupName}
              index={index}
              metadata={metadata}
              onClose={swapAreColumnsOpen}
              pattern={pattern}
              symbolColumns={columns}
              symbols={symbols}
              value={value}
          />
        )}
      </When>
    </Fragment>
  )
}

Row.propTypes = {
  columns: PropTypes.array,
  formulaExpressions: PropTypes.object,
  groupName: PropTypes.string,
  index: PropTypes.number.isRequired,
  isPattern: PropTypes.bool,
  metadata: PropTypes.object,
  pattern: PropTypes.string,
  query: PropTypes.string,
  scrollRef: PropTypes.any,
  setSelectedSymbolIndex: PropTypes.func.isRequired,
  source: PropTypes.string,
  swapIsInfoModalOpen: PropTypes.func.isRequired,
  symbols: PropTypes.object,
  value: PropTypes.any,
}

export default Row
